import type { ChangeEvent } from 'react';
import { splitTime, combineTime, hourOptions, minuteOptions, type AmPm } from '../utils/timeParts';
import './TaskTimePicker.css';

interface TaskTimePickerProps {
  value: string; // stored "HH:MM" (24h), or "" for no time
  onChange: (value: string) => void;
  use24h: boolean;
  disabled?: boolean;
  /** Minute granularity (default 15). */
  minuteStep?: number;
  /**
   * Offer a leading "--" = no time (default true, e.g. an optional task due
   * time). Set false where the field always holds a time and an all-day state
   * is handled elsewhere (e.g. a calendar event's separate all-day toggle).
   */
  allowEmpty?: boolean;
}

/**
 * Time-of-day picker: separate, bounded hour / minute / AM-PM selects (AM-PM
 * only in 12-hour mode). Native selects don't loop — each list ends at its
 * bounds. With `allowEmpty`, the hour's leading "--" means no time; picking an
 * hour starts a time (minute defaults to :00, AM by default in 12h mode).
 */
export function TaskTimePicker({
  value,
  onChange,
  use24h,
  disabled,
  minuteStep = 15,
  allowEmpty = true,
}: TaskTimePickerProps) {
  const parts = splitTime(value, use24h);
  const hasTime = parts !== null;
  const hour = parts?.hour ?? '';
  const minute = parts?.minute ?? 0;
  const ampm: AmPm = parts?.ampm ?? 'AM';

  // Minute options at the configured step, plus the current value if it lands
  // off the grid (e.g. a synced event at :37) so the select still reflects it.
  const minuteOpts = minuteOptions(minuteStep);
  const minuteChoices = hasTime && !minuteOpts.includes(minute)
    ? [...minuteOpts, minute].sort((a, b) => a - b)
    : minuteOpts;

  const handleHour = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === '') {
      onChange(''); // no time
      return;
    }
    onChange(combineTime(Number(e.target.value), hasTime ? minute : 0, hasTime ? ampm : 'AM', use24h));
  };
  const handleMinute = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!hasTime) return;
    onChange(combineTime(Number(hour), Number(e.target.value), ampm, use24h));
  };
  const handleAmPm = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!hasTime) return;
    onChange(combineTime(Number(hour), minute, e.target.value as AmPm, use24h));
  };

  return (
    <div className="task-due-time-group">
      <select
        className="task-due-time"
        value={hasTime ? String(hour) : ''}
        onChange={handleHour}
        disabled={disabled}
        aria-label="Hour"
      >
        {allowEmpty && <option value="">--</option>}
        {hourOptions(use24h).map((h) => (
          <option key={h} value={h}>{use24h ? String(h).padStart(2, '0') : h}</option>
        ))}
      </select>
      <span className="task-due-time-colon">:</span>
      <select
        className="task-due-time"
        value={hasTime ? String(minute) : ''}
        onChange={handleMinute}
        disabled={disabled || !hasTime}
        aria-label="Minute"
      >
        {!hasTime && <option value="">--</option>}
        {minuteChoices.map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      {!use24h && (
        <select
          className="task-due-time"
          value={hasTime ? ampm : ''}
          onChange={handleAmPm}
          disabled={disabled || !hasTime}
          aria-label="AM/PM"
        >
          {!hasTime && <option value="">--</option>}
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      )}
    </div>
  );
}
